(() => {
  const componentName = 'ns-product-linked-information-drawer';
  const overlayOwnerAttribute = 'data-ns-product-linked-information-overlay-owner';

  if (customElements.get(componentName)) return;

  class ProductLinkedInformationDrawer extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.details = this.querySelector('details');
      this.trigger = this.querySelector('summary');
      this.deck = this.querySelector('[data-ns-product-linked-information-deck]');
      this.cards = Array.from(this.querySelectorAll('[data-ns-product-linked-information-card]'));
      this.previousButton = this.querySelector('[data-ns-product-linked-information-previous]');
      this.nextButton = this.querySelector('[data-ns-product-linked-information-next]');
      this.currentOutput = this.querySelector('[data-ns-product-linked-information-current]');
      this.galleryHost = this.closest('[data-ns-product-linked-information-drawer-host]');
      this.overlayStackingHost = this.closest('[data-ns-product-linked-information-overlay-stacking-host]');
      this.desktopQuery = window.matchMedia('(min-width: 768px)');
      this.activeIndex = 0;
      this.originalRole = this.getAttribute('role');
      this.originalAriaModal = this.getAttribute('aria-modal');
      this.originalAriaLabelledBy = this.getAttribute('aria-labelledby');
      this.originalTriggerAriaLabel = this.trigger?.getAttribute('aria-label') ?? null;

      if (!this.details || !this.trigger || !this.deck || this.cards.length === 0) return;

      this.restoreOverlayStackingState();

      const listenerOptions = { signal: this.abortController.signal };
      this.trigger.addEventListener('click', () => this.captureStickyPosition(), listenerOptions);
      this.details.addEventListener('toggle', () => this.handleToggle(), listenerOptions);
      this.addEventListener('click', (event) => this.handleBackdropClick(event), listenerOptions);
      this.addEventListener('keydown', (event) => this.handleKeydown(event), listenerOptions);
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), listenerOptions);
      this.desktopQuery.addEventListener('change', () => this.handleBreakpointChange(), listenerOptions);

      if (this.cards.length > 1) {
        this.previousButton?.addEventListener('click', () => this.showCard(this.activeIndex - 1), listenerOptions);
        this.nextButton?.addEventListener('click', () => this.showCard(this.activeIndex + 1), listenerOptions);
        this.deck.addEventListener('scroll', () => this.scheduleNavigationUpdate(), { ...listenerOptions, passive: true });
      }

      this.syncExpandedState();
      this.updatePresentationMode();
      this.updateNavigation();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.deactivateOverlay({ restoreFocus: false });
      this.restoreGalleryStickyState();

      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      if (this.scrollRestoreFrame) cancelAnimationFrame(this.scrollRestoreFrame);
    }

    syncExpandedState() {
      this.trigger.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
    }

    captureStickyPosition() {
      if (!this.galleryHost || !this.desktopQuery.matches) return;

      const hostStyle = window.getComputedStyle(this.galleryHost);
      const hostTop = this.galleryHost.getBoundingClientRect().top;
      const stickyTop = Number.parseFloat(hostStyle.top) || 0;
      const hostIsActivelySticky = hostStyle.position === 'sticky' && hostTop <= stickyTop + 1;
      const hostIsReleased = this.galleryHost.getAttribute('data-ns-product-linked-information-drawer-open') === this.id;

      this.pendingTriggerTop = this.trigger.getBoundingClientRect().top;
      this.shouldPreserveViewportPosition = hostIsActivelySticky || hostIsReleased;
    }

    handleToggle() {
      this.syncExpandedState();
      this.updatePresentationMode({ preserveDesktopPosition: true, restoreFocus: !this.details.open });
    }

    handleBreakpointChange() {
      this.clearPendingStickyPosition();
      this.updatePresentationMode();
    }

    updatePresentationMode({ preserveDesktopPosition = false, restoreFocus = false } = {}) {
      const shouldUseOverlay = this.details.open && !this.desktopQuery.matches;

      if (shouldUseOverlay) {
        this.restoreGalleryStickyState();
        this.activateOverlay();
      } else {
        this.deactivateOverlay({ restoreFocus });
        this.updateGalleryStickyState(preserveDesktopPosition);
      }
    }

    updateGalleryStickyState(preserveViewportPosition = false) {
      if (!this.galleryHost) return;

      const shouldReleaseSticky = this.details.open && this.desktopQuery.matches;

      if (shouldReleaseSticky) {
        this.galleryHost.setAttribute('data-ns-product-linked-information-drawer-open', this.id);
      } else {
        this.restoreGalleryStickyState();
      }

      if (!preserveViewportPosition || !this.shouldPreserveViewportPosition || this.pendingTriggerTop === undefined) {
        this.clearPendingStickyPosition();
        return;
      }

      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      this.positionFrame = requestAnimationFrame(() => {
        const positionDelta = this.trigger.getBoundingClientRect().top - this.pendingTriggerTop;

        if (Math.abs(positionDelta) > 1) window.scrollBy(0, positionDelta);
        this.clearPendingStickyPosition();
      });
    }

    restoreGalleryStickyState() {
      if (this.galleryHost?.getAttribute('data-ns-product-linked-information-drawer-open') === this.id) {
        this.galleryHost.removeAttribute('data-ns-product-linked-information-drawer-open');
      }
    }

    clearPendingStickyPosition() {
      this.pendingTriggerTop = undefined;
      this.shouldPreserveViewportPosition = false;
    }

    activateOverlay() {
      if (this.overlayActive) return;

      this.overlayActive = true;
      this.setAttribute('data-ns-product-linked-information-overlay-open', '');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-labelledby', this.trigger.id);
      this.trigger.setAttribute('aria-label', `Close ${this.trigger.innerText.trim()}`);
      this.lockPageScroll();
      this.makeBackgroundInert();
      this.elevateOverlayStackingHost();
    }

    deactivateOverlay({ restoreFocus = false } = {}) {
      const ownsOverlayStackingState = this.overlayStackingHost?.getAttribute('data-ns-product-linked-information-overlay-host-open') === this.id;

      if (!this.overlayActive && !this.pageScrollLocked && !this.inertRecords?.length && !ownsOverlayStackingState) return;

      this.overlayActive = false;
      this.restoreOverlayStackingState();
      this.removeAttribute('data-ns-product-linked-information-overlay-open');
      this.restoreAttribute('role', this.originalRole);
      this.restoreAttribute('aria-modal', this.originalAriaModal);
      this.restoreAttribute('aria-labelledby', this.originalAriaLabelledBy);
      this.restoreAttributeOn(this.trigger, 'aria-label', this.originalTriggerAriaLabel);
      this.restoreBackgroundInert();
      this.unlockPageScroll({ restoreFocus });
    }

    elevateOverlayStackingHost() {
      this.overlayStackingHost?.setAttribute('data-ns-product-linked-information-overlay-host-open', this.id);
    }

    restoreOverlayStackingState() {
      if (this.overlayStackingHost?.getAttribute('data-ns-product-linked-information-overlay-host-open') === this.id) {
        this.overlayStackingHost.removeAttribute('data-ns-product-linked-information-overlay-host-open');
      }
    }

    restoreAttribute(name, value) {
      this.restoreAttributeOn(this, name, value);
    }

    restoreAttributeOn(element, name, value) {
      if (!element) return;
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }

    lockPageScroll() {
      const body = document.body;
      const root = document.documentElement;
      const currentOwner = body.getAttribute(overlayOwnerAttribute);

      if (this.pageScrollLocked || (currentOwner && currentOwner !== this.id)) return;

      this.pageScrollLocked = true;
      this.lockedScrollX = window.scrollX;
      this.lockedScrollY = window.scrollY;
      this.originalBodyStyle = body.getAttribute('style');
      this.originalOverlayOwner = currentOwner;

      const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
      const bodyPaddingInlineEnd = Number.parseFloat(window.getComputedStyle(body).paddingInlineEnd) || 0;

      body.setAttribute(overlayOwnerAttribute, this.id);
      body.style.position = 'fixed';
      body.style.top = `${-this.lockedScrollY}px`;
      body.style.left = `${-this.lockedScrollX}px`;
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) body.style.paddingInlineEnd = `${bodyPaddingInlineEnd + scrollbarWidth}px`;
    }

    unlockPageScroll({ restoreFocus = false } = {}) {
      if (!this.pageScrollLocked) {
        if (restoreFocus) this.restoreTriggerFocus();
        return;
      }

      const body = document.body;
      const scrollX = this.lockedScrollX;
      const scrollY = this.lockedScrollY;

      this.restoreStyleAttribute(body, this.originalBodyStyle);
      this.restoreAttributeOn(body, overlayOwnerAttribute, this.originalOverlayOwner);
      this.pageScrollLocked = false;
      window.scrollTo(scrollX, scrollY);

      if (this.scrollRestoreFrame) cancelAnimationFrame(this.scrollRestoreFrame);
      this.scrollRestoreFrame = requestAnimationFrame(() => {
        this.restoreStyleAttribute(body, this.originalBodyStyle);
        window.scrollTo(scrollX, scrollY);
        if (restoreFocus) this.restoreTriggerFocus();
      });
    }

    restoreStyleAttribute(element, value) {
      if (value === null) element.removeAttribute('style');
      else element.setAttribute('style', value);
    }

    restoreTriggerFocus() {
      if (this.isConnected) this.trigger.focus({ preventScroll: true });
    }

    makeBackgroundInert() {
      if (this.inertRecords?.length) return;

      this.inertRecords = [];
      let activeBranch = this;
      let parent = activeBranch.parentElement;

      while (parent) {
        Array.from(parent.children).forEach((sibling) => {
          if (sibling === activeBranch) return;

          this.inertRecords.push({
            element: sibling,
            hadAttribute: sibling.hasAttribute('inert'),
            value: sibling.getAttribute('inert')
          });
          sibling.setAttribute('inert', '');
        });

        if (parent === document.body) break;
        activeBranch = parent;
        parent = parent.parentElement;
      }
    }

    restoreBackgroundInert() {
      this.inertRecords?.forEach(({ element, hadAttribute, value }) => {
        if (!element.isConnected) return;
        if (hadAttribute) element.setAttribute('inert', value ?? '');
        else element.removeAttribute('inert');
      });
      this.inertRecords = [];
    }

    handleBackdropClick(event) {
      if (!this.overlayActive || event.target !== this) return;
      this.details.open = false;
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.details.open) {
        event.preventDefault();
        event.stopPropagation();
        this.captureStickyPosition();
        this.details.open = false;
        if (!this.overlayActive) this.restoreTriggerFocus();
        return;
      }

      if (event.key === 'Tab' && this.overlayActive) this.trapFocus(event);
    }

    trapFocus(event) {
      const focusableElements = Array.from(this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.getClientRects().length > 0 && !element.closest('[inert]'));

      if (focusableElements.length === 0) {
        event.preventDefault();
        this.restoreTriggerFocus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === firstElement || !this.contains(activeElement))) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (activeElement === lastElement || !this.contains(activeElement))) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    handleBlockSelect(event) {
      const selectedBlockId = event.detail?.blockId;
      const selectionIsThisBlock = selectedBlockId === this.dataset.blockId || event.target === this || this.contains(event.target);

      if (!selectionIsThisBlock) return;

      this.captureStickyPosition();
      this.details.open = true;
      this.trigger.focus({ preventScroll: true });
    }

    showCard(index) {
      const targetIndex = Math.max(0, Math.min(index, this.cards.length - 1));
      const card = this.cards[targetIndex];
      const deckRect = this.deck.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const left = this.deck.scrollLeft + cardRect.left - deckRect.left;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.deck.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.activeIndex = targetIndex;
      this.updateNavigation();
    }

    scheduleNavigationUpdate() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);

      this.animationFrame = requestAnimationFrame(() => {
        const deckLeft = this.deck.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - deckLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.activeIndex = closestIndex;
        this.updateNavigation();
      });
    }

    updateNavigation() {
      if (this.currentOutput) this.currentOutput.textContent = String(this.activeIndex + 1);
      if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === this.cards.length - 1;
    }
  }

  customElements.define(componentName, ProductLinkedInformationDrawer);
})();
